import React from "react";

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { SwitchWithLabels, GreenRedSwitchWithLabels } from "./switchElements";

// Mui text field for Redux Form
export const renderTextField = ({
    label,
    input,
    meta: { touched, invalid, error },
    ...custom
}) => {
    return (
        <TextField
            label={label}
            placeholder={label}
            error={touched && invalid}
            helperText={touched && error}
            {...input}
            {...custom}
        />
    )
}

// Mui select for Redux Form
const renderFromHelper = ({ touched, error }) => {
    if (!(touched && error)) {
        return
    } else {
        return <FormHelperText>{touched && error}</FormHelperText>
    }
}

export const renderSelectField = ({
    input,
    label,
    meta: { touched, error },
    labelShrink,
    children,
    ...custom
}) => {
    return (
        <FormControl required={custom.required} error={touched && error} fullWidth sx={{ mt: 2, mb: 1 }}>
            <InputLabel id="select-field">{label}</InputLabel>
            <Select
                {...input}
                {...custom}
                inputProps={{
                    name: input.name,
                }}
                labelId="select-field"
                label={label}
            >
                {children}
            </Select>
            {renderFromHelper({ touched, error })}
        </FormControl>
    )
}

export const AutocompleteInput = ({
    name,
    title,
    subTitle,
    options,
    onChange,
    onInputChange,
    onBlur,
    value,
    error,
    helperText,
    inputWidth,
    ...custom
}) => {

    // let showError = false
    // let errorMessage = null
    // if (_.has(error.validationErrorMessage, name)) {
    //     showError = true
    //     errorMessage = error.validationErrorMessage[name]
    // }

    return (
        <Stack direction="column">
            <Typography variant="subtitle2" fontWeight="bold">
                {title}
            </Typography>
            <Typography variant="caption">
                {subTitle}
            </Typography>
            <Autocomplete
                name={name}
                freeSolo
                disableClearable
                options={options.map(option => option.label)}
                size="small"
                sx={{ width: inputWidth || '20ch' }}
                value={value}
                onChange={onChange}
                onInputChange={onInputChange}
                onBlur={onBlur}
                renderInput={(params) => (
                    <TextField
                        error={error}
                        helperText={helperText}
                        value={value}
                        {...params}
                        InputProps={{
                            ...params.InputProps,
                            type: 'search',
                        }}
                    />
                )}
                {...custom}
            />
        </Stack>
    )
}

export const TextFieldCustom = ({
    name,
    startAdornment,
    endAdornment,
    value,
    onChange,
    onBlur,
    error,
    helperText,
    greenBackground,
    redBackground, // Add redBackground prop
    ...custom    
}) => {
    return (
        <TextField
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            error={error}
            helperText={helperText}
            InputProps={{
                startAdornment: startAdornment && (
                    <InputAdornment position="start">{startAdornment}</InputAdornment>
                ),
                endAdornment: endAdornment && (
                    <InputAdornment position="end">{endAdornment}</InputAdornment>
                ),
            }}
            size="small"
            sx={{
                '& .MuiInputBase-root': {
                    backgroundColor: greenBackground
                        ? '#c8e6c9' // Green background
                        : redBackground
                        ? '#ffcdd2' // Red background
                        : 'white',  // Default background
                },
            }}
            {...custom}
        />
    )
}

export const TextfieldInput = ({
    name,
    title,
    subTitle,
    startAdornment,
    endAdornment,
    value,
    onChange,
    onBlur,
    error,
    helperText,
    ...custom
}) => {

    // let showError = false
    // let errorMessage = null
    // if (_.has(error.validationErrorMessage, name)) {
    //     showError = true
    //     errorMessage = error.validationErrorMessage[name]
    // }


    return (
        <Stack direction="column">
            <Typography variant="subtitle2" fontWeight="bold">
                {title}
            </Typography>
            <Typography variant="caption">
                {subTitle}
            </Typography>
            <TextFieldCustom
                name={name}
                sx={{ width: '20ch' }}
                value={value}
                startAdornment={startAdornment}
                endAdornment={endAdornment}                
                onChange={onChange}
                onBlur={onBlur}
                error={error}
                helperText={helperText}
                size="small"
                {...custom}
            />
            {/* <TextField
                name={name}
                sx={{ width: '20ch' }}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error}
                helperText={helperText}
                InputProps={{
                    startAdornment: startAdornment && (
                        <InputAdornment position="start">{startAdornment}</InputAdornment>
                    ),
                    endAdornment: endAdornment && (
                        <InputAdornment position="end">{endAdornment}</InputAdornment>
                    ),
                }}
                size="small"
                {...custom}
            /> */}
        </Stack>
    )
}

export const DatePickerInput = ({
    name,
    title,
    subTitle,
    views,
    format,
    value,
    onChange,
    error,
    helperText
}) => {
    // let showError = false
    // let errorMessage = null
    // if (_.has(error.validationErrorMessage, name)) {
    //     showError = true
    //     errorMessage = error.validationErrorMessage[name]
    // }

    return (
        <Stack direction="column">
            <Typography variant="subtitle2" fontWeight="bold">
                {title}
            </Typography>
            <Typography variant="caption">
                {subTitle}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    name={name}
                    views={views}
                    slotProps={{ textField: { size: 'small', error: error, helperText: helperText } }}
                    sx={{ width: '20ch' }}
                    format={format}
                    value={value}
                    onChange={onChange}
                />
            </LocalizationProvider>
        </Stack>
    )
}

export const CompactSwitch = ({
    name,
    leftLabel,
    rightLabel,
    value,
    onChange,
    disabled
}) => {
    return (
        <SwitchWithLabels
            name={name}
            leftLabel={leftLabel}
            rightLabel={rightLabel}
            checked={value}
            onChange={onChange}
            disabled={disabled}
        />
    )
}

export const GreenRedCompactSwitch = ({
    name,
    leftLabel,
    rightLabel,
    value,
    onChange,
    disabled
}) => {
    return (
        <GreenRedSwitchWithLabels
            name={name}
            leftLabel={leftLabel}
            rightLabel={rightLabel}
            checked={value}
            onChange={onChange}
            disabled={disabled}
        />
    )
}